import { useTranslation } from 'react-i18next';
import { useCurrency } from '@/Hooks/useCurrency';
import type { ResourceRow, SessionRow } from '../lib/sessions';
import { ExtendSessionDialog, StartSessionDialog, StopSessionDialog } from './SessionDialogs';
import { KidsCheckInDialog, KidsCheckOutDialog } from './KidsAreaDialogs';
import { QuickOrderDialog } from './QuickOrderDialog';

/**
 * What can be asked of a resource from the floor.
 *
 * These mirror `resource-sessions.controller.ts` one for one — `POST
 * /resource-sessions`, `/:id/pause`, `/:id/resume`, `/:id/extend`, `/:id/stop`
 * — plus the two that are not session moves at all: ordering food against a
 * running meter, and opening the resource's own panel.
 */
export type ResourceAction =
  | 'order'
  | 'start'
  | 'pause'
  | 'resume'
  | 'extend'
  | 'stop'
  | 'checkOutByTag'
  | 'details'
  // C-16 — the resource itself, not the session on it.
  | 'edit'
  | 'retire'
  // §10.2 — what the resource costs. Not a session move either, and no longer a
  // dialog: a rate belongs to a card that ten consoles share, so this action
  // *goes to* the card — or, when the resource charges from none, to the
  // resource's own form, which is where `pricingListId` lives. `FloorMap`
  // resolves which; there is nothing to open here.
  | 'pricing';

export type ResourceDialogState =
  | { kind: 'start'; resource: ResourceRow }
  | { kind: 'extend'; session: SessionRow }
  | { kind: 'stop'; session: SessionRow }
  // §11 — a kids area needs a guardian and a wristband, which the generic
  // start/stop pair cannot carry, so it gets its own two dialogs.
  | { kind: 'kidsCheckIn'; resource: ResourceRow }
  | { kind: 'kidsCheckOut'; resource: ResourceRow; session: SessionRow | null }
  // S-17 / ADR-003 — food and drink for the guest on a meter. This floor may
  // have no tables at all, so the order is posted against the check the meter
  // was opened against rather than against a table session.
  | { kind: 'order'; resource: ResourceRow; session: SessionRow }
  | null;

/**
 * The single place an action becomes a dialog.
 *
 * §11 is the reason it is a function and not a `switch` at each call site: a
 * kids area must never take the generic start/stop pair, because those cannot
 * capture the child, the guardian and the wristband. Routing it here means the
 * map menu and the panel cannot disagree, and neither can bypass the check-in
 * flow by accident.
 */
export function resourceDialogFor(
  action: ResourceAction,
  resource: ResourceRow,
  session: SessionRow | null,
): ResourceDialogState {
  const isKids = resource.type === 'KIDS_AREA';
  switch (action) {
    case 'start':
      return isKids ? { kind: 'kidsCheckIn', resource } : { kind: 'start', resource };
    case 'stop':
      if (!session) return null;
      return isKids ? { kind: 'kidsCheckOut', resource, session } : { kind: 'stop', session };
    case 'checkOutByTag':
      return { kind: 'kidsCheckOut', resource, session: null };
    case 'extend':
      return session ? { kind: 'extend', session } : null;
    case 'order':
      return session ? { kind: 'order', resource, session } : null;
    default:
      // `pause`/`resume` post straight away, `details` opens the panel, and
      // `pricing` is a route rather than a dialog; none has anything to ask
      // first.
      return null;
  }
}

interface Props {
  dialog: ResourceDialogState;
  /** §11 — check-out-by-tag needs every band currently in the area. */
  sessionsByResource: Map<string, SessionRow[]>;
  /**
   * I-13 — the branch whose currency and price list this resource bills in.
   * A venue can run two branches with two currencies, so this is asked per
   * resource rather than taken from whichever branch is selected.
   */
  branchIdFor: (resource: ResourceRow) => string | null;
  onClose: () => void;
  onDone: () => void;
}

/** Every dialog a resource action can open, in one place, driven by one state. */
export function ResourceDialogs({
  dialog,
  sessionsByResource,
  branchIdFor,
  onClose,
  onDone,
}: Props) {
  const { t } = useTranslation();
  // I-13 — each resource and each running meter names its own currency,
  // resolved server-side from its branch. This is only the fallback for a
  // payload that predates the field.
  const { code: currencyCode } = useCurrency();

  return (
    <>
      <StartSessionDialog
        open={dialog?.kind === 'start'}
        resource={dialog?.kind === 'start' ? dialog.resource : null}
        currency={(dialog?.kind === 'start' ? dialog.resource.currency : null) ?? currencyCode}
        onClose={onClose}
        onDone={onDone}
      />
      <ExtendSessionDialog
        open={dialog?.kind === 'extend'}
        session={dialog?.kind === 'extend' ? dialog.session : null}
        onClose={onClose}
        onDone={onDone}
      />
      <StopSessionDialog
        open={dialog?.kind === 'stop'}
        session={dialog?.kind === 'stop' ? dialog.session : null}
        currency={(dialog?.kind === 'stop' ? dialog.session.currency : null) ?? currencyCode}
        onClose={onClose}
        onDone={onDone}
      />
      <KidsCheckInDialog
        open={dialog?.kind === 'kidsCheckIn'}
        resource={dialog?.kind === 'kidsCheckIn' ? dialog.resource : null}
        currency={(dialog?.kind === 'kidsCheckIn' ? dialog.resource.currency : null) ?? currencyCode}
        onClose={onClose}
        onDone={onDone}
      />
      <KidsCheckOutDialog
        open={dialog?.kind === 'kidsCheckOut'}
        resource={dialog?.kind === 'kidsCheckOut' ? dialog.resource : null}
        sessions={
          dialog?.kind === 'kidsCheckOut' ? (sessionsByResource.get(dialog.resource.id) ?? []) : []
        }
        session={dialog?.kind === 'kidsCheckOut' ? dialog.session : null}
        currency={
          (dialog?.kind === 'kidsCheckOut' ? dialog.resource.currency : null) ?? currencyCode
        }
        onClose={onClose}
        onDone={onDone}
      />
      {/* ADR-003 — the order carries a fulfillment context, not a table. A PS5
          session's snacks join that meter's own check, exactly as a child's do:
          same `OrderTarget`, same `orderBodyFor()`. A meter opened with no table
          and no open check has nothing to post against. */}
      {dialog?.kind === 'order' && dialog.session.checkId && (
        <QuickOrderDialog
          open
          target={{
            kind: 'check',
            checkId: dialog.session.checkId,
            branchId: branchIdFor(dialog.resource),
            label: dialog.resource.name,
            context: t('spaces.resources.orderFoodHint', { name: dialog.resource.name }),
          }}
          onClose={onClose}
          onOrdered={onDone}
        />
      )}
    </>
  );
}
